import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { keyframes, styled } from '@mui/system';
import {
  BrightnessHighTwoTone,
  BrightnessMediumTwoTone,
  BrightnessLowTwoTone,
} from '@mui/icons-material';

// Flashing animation for high priority
const flash = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

// Styled flashing chip
const FlashingChip = styled(Chip)(({ theme }) => ({
  animation: `${flash} 1s infinite`,
}));

type PriorityChipProps = {
  priorityLevel?: number;
  label?: string; // optional label
};

const PriorityChip: React.FC<PriorityChipProps> = ({ priorityLevel, label }) => {
  if (!priorityLevel) return null;

  let icon;
  let color: "warning" | "error" | "default" = "default";
  let tooltip;

  switch (priorityLevel) {
    case 3:
      icon = <BrightnessHighTwoTone sx={{ color: 'white', fontSize: 24 }} />;
      color = 'error';
      tooltip = 'High Priority';
      break;
    case 2:
      icon = <BrightnessMediumTwoTone sx={{ color: 'white', fontSize: 24 }} />;
      color = 'warning';
      tooltip = 'Medium Priority';
      break;
    case 1:
      icon = <BrightnessLowTwoTone sx={{ color: 'white', fontSize: 24 }} />;
      color = 'default';
      tooltip = 'Low Priority';
      break;
    default:
      return null;
  }

  const ChipComponent = priorityLevel === 3 ? FlashingChip : Chip;

  return (
    <ChipComponent
      size="small"
      color={color}
      icon={icon}
      title={tooltip}
      label={label}
      sx={{
        width: label ? 'auto' : 24,
        height: 24,
        minWidth: label ? 'auto' : 0,
        borderRadius: '50%',
        padding: 0,
        '.MuiChip-label': { display: label ? 'inline' : 'none' },
        '.MuiChip-icon': {
          marginLeft: 0,
          marginRight: label ? 0.5 : 0,
        },
      }}
    />
  );
};

export default PriorityChip;
